import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Upload, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import LogoImage from "@/components/LogoImage";
import { deleteAssetIfInternal } from "./BrandingTab";

const SLOTS = [
  { key: "header", label: "Header Top Banner", hint: "728x90 desktop / 320x50 mobile" },
  { key: "sidebar", label: "Sidebar Banner", hint: "Medium Rectangle 300x250" },
  { key: "infeed", label: "In-Feed Mercato", hint: "Rectangle 300x250 tra le schede piloti" },
  { key: "footer", label: "Footer Banner", hint: "728x90 sopra il piè di pagina" },
];

export default function SponsorsTab() {
  const qc = useQueryClient();
  const { data: ads = {} } = useQuery({
    queryKey: ["ads"],
    queryFn: async () => (await api.get("/ads")).data,
  });
  const [forms, setForms] = useState({});

  useEffect(() => {
    if (Object.keys(forms).length === 0 && Object.keys(ads).length > 0) {
      const init = {};
      SLOTS.forEach(({ key }) => {
        const a = ads[key] || {};
        init[key] = {
          enabled: a.enabled ?? true,
          image_url: a.image_url || "",
          link: a.link || "",
          html: a.html || "",
        };
      });
      setForms(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  const setSlot = (slot, k, v) => setForms((f) => ({ ...f, [slot]: { ...f[slot], [k]: v } }));

  const pickFile = async (slot, file) => {
    if (!file) return;
    if (file.size > 10000000) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-asset", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSlot(slot, "image_url", data.url);
      toast.success("Immagine caricata sullo storage permanente — premi Salva Spazio per pubblicarla");
    } catch (e) {
      toast.error(errMsg(e, "Upload fallito"));
    }
  };

  const clearImage = async (slot) => {
    await deleteAssetIfInternal(forms[slot]?.image_url);
    setSlot(slot, "image_url", "");
    toast.success("Immagine rimossa — premi Salva Spazio per confermare");
  };

  const save = async (slot) => {
    try {
      await api.put(`/admin/ads/${slot}`, forms[slot]);
      toast.success(`Spazio "${slot}" aggiornato`);
      qc.invalidateQueries({ queryKey: ["ads"] });
    } catch (e) {
      toast.error(errMsg(e, "Salvataggio fallito"));
    }
  };

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      {SLOTS.map(({ key, label, hint }) => {
        const f = forms[key] || { enabled: true, image_url: "", link: "", html: "" };
        return (
          <div key={key} className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest">{label}</h3>
                <p className="text-[10px] text-gray-500">{hint}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-body-sport text-[10px] tracking-widest text-gray-500 uppercase">
                  {f.enabled ? "Attivo" : "Disattivo"}
                </span>
                <Switch
                  data-testid={`ad-toggle-${key}`}
                  checked={f.enabled}
                  onCheckedChange={(v) => setSlot(key, "enabled", v)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-lg border border-dashed border-[#3F3F4C] bg-[#121214] flex items-center justify-center overflow-hidden shrink-0"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickFile(key, e.dataTransfer.files?.[0]);
                }}
                title={`Trascina qui il banner (${hint})`}
              >
                {f.image_url ? (
                  <LogoImage src={f.image_url} alt={label} hideOnError className="max-w-full max-h-full object-contain" />
                ) : (
                  <Upload className="w-4 h-4 text-gray-600" />
                )}
              </div>
              <div className="flex-1 space-y-1.5 min-w-0">
                <div className="flex items-center gap-1.5">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    data-testid={`ad-file-${key}`}
                    onChange={(e) => pickFile(key, e.target.files?.[0])}
                    className="block w-full text-[11px] text-gray-400 file:mr-2 file:rounded-lg file:border-0 file:bg-[#FF1E27] file:px-3 file:py-1.5 file:text-[10px] file:font-bold file:text-white file:uppercase file:tracking-widest hover:file:bg-[#CC1118] file:cursor-pointer"
                  />
                  {f.image_url && (
                    <button
                      type="button"
                      data-testid={`ad-delete-${key}`}
                      onClick={() => clearImage(key)}
                      title="Elimina immagine"
                      className="shrink-0 p-2 rounded-lg border border-[#3F3F4C] text-gray-500 hover:text-[#FF1E27] hover:border-[#FF1E27] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500">
                  Dimensione consigliata: <span className="text-gray-400 font-bold">{hint}</span> — JPG, PNG, WEBP o SVG, max 10 MB
                </p>
              </div>
            </div>
            <input
              data-testid={`ad-image-${key}`}
              className={inputCls}
              placeholder="...oppure incolla URL immagine sponsor (opzionale)"
              value={f.image_url}
              onChange={(e) => setSlot(key, "image_url", e.target.value)}
            />
            <input
              data-testid={`ad-link-${key}`}
              className={inputCls}
              placeholder="Link di destinazione (opzionale)"
              value={f.link}
              onChange={(e) => setSlot(key, "link", e.target.value)}
            />
            <textarea
              data-testid={`ad-html-${key}`}
              className={`${inputCls} font-time text-xs`}
              rows={3}
              placeholder="Codice HTML/Script circuito pubblicitario (AdSense, Ezoic...)"
              value={f.html}
              onChange={(e) => setSlot(key, "html", e.target.value)}
            />
            <button
              data-testid={`ad-save-${key}`}
              onClick={() => save(key)}
              className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2 font-body-sport font-bold text-xs tracking-widest text-white uppercase"
            >
              <Save className="w-3.5 h-3.5" /> Salva Spazio
            </button>
          </div>
        );
      })}
    </div>
  );
}
